import React, { useState } from 'react';
import { Card, Space, Statistic, Row, Col, Select } from 'antd';

interface SalesDataPoint {
  date: string;
  [key: string]: string | number;
}

interface SalesChartProps {
  data?: SalesDataPoint[];
  loading?: boolean;
  groupBy?: 'service' | 'seller';
  seriesKeys?: string[]; // 系列值（如销售ID或名称）
  seriesLabels?: { value: string; label: string }[]; // 系列显示名称
  title?: string;
  sellerFilterOptions?: { value: string; label: string }[];
  sellerFilterValue?: string;
  onSellerFilterChange?: (val: string) => void;
  clientTypes?: Array<{ type: string; count: number }>;
  showSellerSelector?: boolean;
}

const SalesChart: React.FC<SalesChartProps> = ({
  data = [],
  loading = false,
  groupBy = 'service',
  seriesKeys = [],
  seriesLabels = [],
  title = '销售业绩管理',
  sellerFilterOptions = [],
  sellerFilterValue,
  onSellerFilterChange,
  clientTypes = [],
  showSellerSelector = true,
}) => {
  const fallbackSeries = seriesKeys[0] || seriesLabels[0]?.value;
  const [selectedSeries, setSelectedSeries] = useState<string[]>(fallbackSeries ? [fallbackSeries] : []);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; content: string } | null>(null);

  // 当可选系列变更时，默认选中第一个
  // 保持用户选择，不自动设置默认销售
  React.useEffect(() => {
    setSelectedSeries(prev => (Array.isArray(prev) ? prev : []));
  }, [seriesKeys.join(','), seriesLabels.map(l => l.value).join(',')]);
  // 使用传入的数据或模拟数据
  const mockData: SalesDataPoint[] = [
    { date: '8/25', salesA: 280, salesB: 180, salesC: 120 },
    { date: '8/26', salesA: 300, salesB: 160, salesC: 130 },
    { date: '8/27', salesA: 250, salesB: 200, salesC: 140 },
    { date: '8/28', salesA: 280, salesB: 180, salesC: 130 },
    { date: '8/29', salesA: 200, salesB: 200, salesC: 150 },
    { date: '8/30', salesA: 250, salesB: 220, salesC: 180 },
    { date: '8/31', salesA: 290, salesB: 280, salesC: 190 },
    { date: '9/01', salesA: 320, salesB: 300, salesC: 200 },
  ];

  const chartData = data.length > 0 ? data : mockData;

  // 动态提取数值字段，根据数据类型和分组方式
  const getNumericKeys = () => {
    if (chartData.length === 0) return ['salesA', 'salesB', 'salesC'];
    const firstItem = chartData[0];
    const allowSeller = selectedSeries[0] || seriesKeys[0];
    const metrics = ['amount', 'profit', 'count'];
    if (groupBy === 'seller' && allowSeller) {
      return metrics
        .map(m => `${allowSeller}_${m}`)
        .filter(k => typeof firstItem[k] === 'number');
    }

    const suffixes = ['_amount', '_profit', '_count'];
    return Object.keys(firstItem).filter(key =>
      key !== 'date' && typeof firstItem[key] === 'number' && suffixes.some(suf => key.endsWith(suf))
    );
  };

  const numericKeys = getNumericKeys();
  const maxValue = Math.max(...chartData.flatMap(d =>
    numericKeys.map(key => typeof d[key] === 'number' ? d[key] as number : 0)
  ), 1);

  // 计算总计
  const totals = numericKeys.reduce<Record<string, number>>((acc, key) => {
    acc[key] = chartData.reduce((sum, item) => sum + (typeof item[key] === 'number' ? item[key] as number : 0), 0);
    return acc;
  }, {});

  const renderSimpleChart = () => (
    <div style={{ height: '280px', position: 'relative' }}>
      {tooltip && (
        <div
          style={{
            position: 'absolute',
            left: tooltip.x,
            top: tooltip.y - 40,
            background: 'rgba(0,0,0,0.75)',
            color: '#fff',
            padding: '6px 10px',
            borderRadius: 4,
            fontSize: 12,
            pointerEvents: 'none',
            transform: 'translate(-50%, -50%)',
            whiteSpace: 'nowrap',
            zIndex: 2,
          }}
        >
          {tooltip.content}
        </div>
      )}
      <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
        {/* 绘制网格线 */}
        <defs>
          <pattern id="grid" width="40" height="25" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 25" fill="none" stroke="#f0f0f0" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />

        {/* 动态绘制折线 */}
        {numericKeys.map((key, keyIndex) => {
          const colors = ['#ff9800', '#2196f3', '#4caf50', '#9c27b0', '#f44336'];
          const color = colors[keyIndex % colors.length];
          const metricLabelMap: Record<string, string> = {
            amount: '成交额',
            profit: '毛利润',
            count: key === 'client_count' ? '新增客户数' : '订单数量',
          };
          const metric = key.endsWith('_profit') ? 'profit' : key.endsWith('_count') ? 'count' : 'amount';

          return (
            <g key={key}>
              <polyline
                fill="none"
                stroke={color}
                strokeWidth="2"
                points={chartData.map((d, i) => {
                  const value = typeof d[key] === 'number' ? d[key] as number : 0;
                  return `${i * 60 + 40},${240 - (value / maxValue) * 180}`;
                }).join(' ')}
              />
              {/* 绘制数据点 */}
              {chartData.map((d, i) => {
                const value = typeof d[key] === 'number' ? d[key] as number : 0;
                const dateLabel = d.date;
                return (
                  <g key={`${key}-${i}`}>
                    <circle
                      cx={i * 60 + 40}
                      cy={240 - (value / maxValue) * 180}
                      r="4"
                      fill={color}
                      onMouseEnter={() => setTooltip({
                        x: i * 60 + 40,
                        y: 240 - (value / maxValue) * 180,
                        content: `${dateLabel} | ${metricLabelMap[metric]}: ${value}`
                      })}
                      onMouseLeave={() => setTooltip(null)}
                    />
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* X轴标签 */}
        {chartData.map((d, i) => (
          <text key={`label-${i}`} x={i * 60 + 40} y={270} textAnchor="middle" fontSize="12" fill="#666">
            {d.date}
          </text>
        ))}

        {/* Y轴刻度 */}
        {[0, 100, 200, 300].map(value => (
          <g key={value}>
            <line x1="30" y1={240 - (value / maxValue) * 180} x2="35" y2={240 - (value / maxValue) * 180} stroke="#999" />
            <text x="25" y={240 - (value / maxValue) * 180 + 4} textAnchor="end" fontSize="12" fill="#666">
              {value}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );

  return (
    <Card
      loading={loading}
      title={title}
      style={{ height: 500 }}
    >
      {/* 销售筛选（组件外部传入或 seller 模式） + 图例 */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        {showSellerSelector && (onSellerFilterChange && sellerFilterOptions.length > 0) ? (
          <div style={{ minWidth: 200 }}>
            <span style={{ marginRight: 8 }}>销售：</span>
            <Select
              placeholder="选择销售"
              value={sellerFilterValue || undefined}
              onChange={(val: string) => onSellerFilterChange(val || '')}
              options={sellerFilterOptions}
              style={{ minWidth: 200 }}
              allowClear
              showSearch
            />
          </div>
        ) : (
          showSellerSelector && groupBy === 'seller' && ((seriesKeys.length + seriesLabels.length) > 0) && (
            <div style={{ minWidth: 200 }}>
              <span style={{ marginRight: 8 }}>销售：</span>
              <Select
                placeholder="选择销售"
                value={selectedSeries[0] || undefined}
                onChange={(val: string) => setSelectedSeries(val ? [val] : [])}
                options={(seriesLabels.length ? seriesLabels : seriesKeys.map(name => ({ label: name, value: name })))}
                style={{ minWidth: 200 }}
                allowClear
                showSearch
              />
            </div>
          )
        )}
        <Space size="large">
          {numericKeys.map((key, index) => {
            const colors = ['#ff9800', '#2196f3', '#4caf50'];
            const color = colors[index % colors.length];
            const metricLabelMap: Record<string, string> = {
              amount: '成交额',
              profit: '毛利润',
              count: key === 'client_count' ? '新增客户数' : '订单数量',
          };
            const metric = key.endsWith('_profit') ? 'profit' : key.endsWith('_count') ? 'count' : 'amount';

            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 12,
                  height: 12,
                  backgroundColor: color,
                  borderRadius: '50%'
                }} />
                <span style={{ fontSize: 14 }}>{metricLabelMap[metric]}</span>
              </div>
            );
          })}
        </Space>
      </div>

      {/* 简化图表 */}
      {renderSimpleChart()}

      {clientTypes && clientTypes.length > 0 && (
        <Row gutter={16} style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
          {clientTypes.map((item, idx) => {
            return (
              <Col span={8} key={item.type}>
                <Statistic
                  title={'客户等级 ' + item.type}
                  value={item.count}
                  precision={0}
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
            )}
          )}
        </Row>
      )}

      {/* 底部统计 */}
      {title === '销售业绩管理' && (
        <Row gutter={16} style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
          {['amount', 'profit', 'count'].map((metric) => {
            const labelMap: Record<string, string> = {
              amount: '总成交额',
              profit: '总毛利润',
              count: '总订单数',
            };
            const key = `${selectedSeries[0] || seriesKeys[0] || ''}_${metric}`;
            return (
              <Col span={8} key={metric}>
                <Statistic
                  title={labelMap[metric]}
                  value={totals[key] || 0}
                  precision={0}
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
            );
          })}
        </Row>
      )}
    </Card>
  );
};

export default SalesChart;
